import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ThemeService } from './theme.service';

@Component({
  selector: 'app-theme-toggle',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      class="theme-toggle-btn"
      (click)="toggleTheme()"
      [attr.aria-label]="isDarkMode() ? 'Switch to light mode' : 'Switch to dark mode'"
      title="Toggle theme"
    >
      <i [class]="isDarkMode() ? 'pi pi-sun' : 'pi pi-moon'" aria-hidden="true"></i>
    </button>
  `,
  styles: [`
    .theme-toggle-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: none;
      border-radius: 50%;
      background-color: transparent;
      color: var(--icon-color);
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: var(--bg-hover);
        color: var(--theme-primary-color);
        transform: translateY(-1px);

        i {
          transform: rotate(180deg);
        }
      }

      &:active {
        transform: translateY(0);
      }

      &:focus-visible {
        outline: 2px solid var(--success-color);
        outline-offset: 2px;
      }

      i {
        font-size: 1.25rem;
        transition: transform 0.3s ease;
      }
    }
  `],
})
export class ThemeToggleComponent implements OnInit, OnDestroy {
  protected readonly themeService = inject(ThemeService);
  protected readonly destroy$ = new Subject<void>();

  readonly isDarkMode = signal(false);

  ngOnInit(): void {
    this.themeService.theme$.pipe(takeUntil(this.destroy$)).subscribe(theme => {
      this.isDarkMode.set(theme === 'dark');
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected toggleTheme(): void {
    this.themeService.toggleTheme();
  }
}
